import React from 'react'
import { Toaster, toast } from 'sonner'
import UINotification from 'src/features/notifications/components/Notification'
import Alert from 'src/components/Alert'
import { useIntl } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import NotificationCoordinatorService from 'src/services/NotificationCoordinatorService'
import DesktopNotificationService from 'src/services/DesktopNotificationService'

export const Toast = () => {
  return (
    <Toaster
      position='top-right'
      richColors
      duration={4000}
      closeButton={false}
      toastOptions={{
        style: {
          background: 'white',
          border: '1px solid #e2e8f0',
          color: 'black'
        },
        className: 'custom-toast'
      }}
    />
  )
}

export const showAlertToast = (message, options = {}) =>
  toast.custom(() => <Alert type='alert' message={message} size={28} />, {
    ...options,
    style: {
      padding: 0,
      background: 'transparent',
      border: '1px solid rgba(36, 45, 63, 0.4)',
      boxShadow: '0 0 20px 0 rgba(36, 45, 63, 0.24)',
      ...options.style
    }
  })

const ToastContent = ({ notification, onClose }) => {
  const intl = useIntl()
  return (
    <ToastWrapper>
      <CloseButton
        onClick={e => {
          e.stopPropagation()
          onClose()
        }}
        title={intl.formatMessage({ id: 'notifications.closeNotification' })}
      />
      <UINotification
        notification={notification}
        isToast={true}
        onClick={e => {
          e.stopPropagation()
        }}
      />
    </ToastWrapper>
  )
}

export const showToast = async notification => {
  const notificationCoordinator = NotificationCoordinatorService.getInstance()

  try {
    const decision = await notificationCoordinator.shouldShowNotification()

    if (decision.showToast) {
      const toastId = toast.custom(
        () => (
          <ToastContent
            notification={notification}
            onClose={() => toast.dismiss(toastId)}
          />
        ),
        {
          style: {
            background: 'white',
            border: '1px solid #e2e8f0',
            padding: 0
          },
          duration: 4000,
          dismissible: true
        }
      )

      notificationCoordinator.markNotificationShown()
      return toastId
    } else if (decision.showDesktop) {
      const desktopNotificationService = DesktopNotificationService.getInstance()

      if (desktopNotificationService.hasPermission()) {
        try {
          desktopNotificationService.show({
            title:
              notification.desktopNotification?.title || notification.subject,
            body: notification.desktopNotification?.body,
            icon: notification.desktopNotification?.icon,
            silent: true,
            autoClose: 5000,
            onClick: () => {
              if (notification.cta?.url) {
                window.focus()
                window.location.href = notification.cta.url
              }
            },
            onError: error => {
              console.error('Desktop notification error:', error)
            }
          })

          notificationCoordinator.markNotificationShown()
          return
        } catch (error) {
          console.error(
            '❌ [Toast] Błąd podczas wyświetlania desktop notification:',
            error
          )
          return showToastFallback(notification)
        }
      } else if (window.Notification?.permission === 'default') {
        try {
          const permission = await window.Notification.requestPermission()
          if (permission === 'granted') {
            return showToast(notification)
          } else {
            return showToastFallback(notification)
          }
        } catch (error) {
          return showToastFallback(notification)
        }
      } else {
        return showToastFallback(notification)
      }
    }
  } catch (error) {
    return showToastFallback(notification)
  }
}

const showToastFallback = notification => {
  const toastId = toast.custom(
    () => (
      <ToastContent
        notification={notification}
        onClose={() => toast.dismiss(toastId)}
      />
    ),
    {
      style: {
        background: 'white',
        border: '1px solid #e2e8f0',
        padding: 0
      },
      duration: 4000,
      dismissible: true
    }
  )
  return toastId
}

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.black.alpha(0.6).hex()};
  font-size: 16px;
  line-height: 1;
  z-index: 10;

  &:hover {
    background-color: ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};
    color: ${({ theme }) => theme.colors.black.hex()};
  }

  &:before {
    content: '×';
    font-weight: bold;
    margin-bottom: 4px;
  }
`

const ToastWrapper = styled.div`
  position: relative;
  width: 100%;
  pointer-events: auto;
`

export default Toast
